import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, X, Ticket, Mail, RefreshCw, Send, Check, Navigation, MapPin, CheckCircle
} from "lucide-react";
import { API_URL } from "@/config";
import { useTickets } from "@/context/TicketContext";
import { TicketFormModal } from "@/components/TicketFormModal";
import { CloseTicketModal } from "@/components/CloseTicketModal";

const AllActiveTicketsPage = () => {
  const navigate = useNavigate();
  const { tickets, updateTicket, closeTicket } = useTickets();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [emailNotification, setEmailNotification] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Force refresh tickets from API
  const handleRefresh = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets`);
      if (res.ok) {
        setLastRefresh(new Date());
      }
    } catch (err) {}
    window.location.reload();
  };

  const statusOrder = { 'Open': 0, 'In Progress': 1, 'Pending': 2, 'Dispatched': 3, 'Dispatch Acknowledged': 4, 'En Route': 5, 'On Site': 6, 'Work Complete': 7 };
  const activeTickets = tickets
    .filter(t => t.status !== 'Closed')
    .sort((a, b) => (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99));

  const handleEditClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleCloseClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowCloseModal(true);
  };

  const handleUpdateTicket = (formData) => {
    updateTicket(formData.ticketId, formData);
    setShowEditModal(false);
    setSelectedTicket(null);
  };

  const handleDispatchClick = async (ticket) => {
    const dispatchedAt = new Date().toISOString();
    const result = await updateTicket(ticket.ticketId, { 
      status: 'Dispatched',
      dispatchedAt: dispatchedAt
    });
    if (result?.emailSent) {
      setEmailNotification(`Dispatch email sent to ${result.emailTo}`);
      setTimeout(() => setEmailNotification(null), 5000);
    }
  };

  const handleCloseTicket = (ticketId, closingComments) => {
    closeTicket(ticketId, closingComments);
    setShowCloseModal(false);
    setSelectedTicket(null);
  };

  return (
    <div className="app-container" data-testid="all-active-tickets-page">
      <header className="app-header">
        <div className="header-left">
          <Zap size={24} className="header-icon" />
          <h1>SERVICE CONSOLE</h1>
        </div>
      </header>
      <button className="close-page-btn" data-testid="back-to-dashboard" onClick={() => navigate('/')} style={{position:'fixed',top:'16px',right:'16px',zIndex:100}}>
        <X size={20} />
      </button>

      <main className="main-content">
        {emailNotification && (
          <div className="email-notification" data-testid="email-notification">
            <Mail size={16} />
            <span>{emailNotification}</span>
          </div>
        )}
        <div className="device-page-header">
          <h2 className="device-page-title" data-testid="page-title">
            ALL ACTIVE TICKETS
          </h2>
          <span className="device-count">{activeTickets.length} ticket{activeTickets.length !== 1 ? 's' : ''}</span>
          <button className="refresh-btn" onClick={handleRefresh} data-testid="refresh-btn" title="Refresh tickets">
            <RefreshCw size={16} />
          </button>
        </div>

        {activeTickets.length === 0 ? (
          <div className="no-tickets-message" data-testid="no-tickets">
            <p>No active tickets found.</p>
          </div>
        ) : (
          <div className="tickets-list" data-testid="all-tickets-list">
            {activeTickets.map(ticket => (
              <div key={ticket.ticketId} className="ticket-card" data-testid={`ticket-card-${ticket.ticketId}`}>
                <div className="ticket-card-header">
                  <div className="ticket-id-section">
                    <Ticket size={18} className="ticket-icon" />
                    <span className="ticket-id">{ticket.ticketId}</span>
                    <span className={`ticket-status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>{ticket.status}</span>
                    <span className={`ticket-priority-badge ${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                  </div>
                  <div className="ticket-header-right">
                    <div className="ticket-actions-header">
                      <button className="ticket-action-btn edit" onClick={() => handleEditClick(ticket)}>EDIT</button>
                      {ticket.status !== 'Dispatched' && ticket.status !== 'Dispatch Acknowledged' && (
                        <button className="ticket-action-btn dispatch" onClick={() => handleDispatchClick(ticket)}>DISPATCH</button>
                      )}
                      <button className="ticket-action-btn close-ticket" onClick={() => handleCloseClick(ticket)}>CLOSE</button>
                    </div>
                  </div>
                </div>
                <div className="ticket-card-body">
                  <div className="ticket-detail-grid">
                    <div className="ticket-detail"><span className="detail-label">Subscriber:</span><span className="detail-value">{ticket.subscriberName}</span></div>
                    <div className="ticket-detail"><span className="detail-label">Device:</span><span className="detail-value">{ticket.deviceId}</span></div>
                    <div className="ticket-detail"><span className="detail-label">Type:</span><span className="detail-value">{ticket.deviceType}</span></div>
                    <div className="ticket-detail"><span className="detail-label">Issue:</span><span className="detail-value">{ticket.issueType}</span></div>
                    <div className="ticket-detail"><span className="detail-label">Location:</span><span className="detail-value">{ticket.location}</span></div>
                    <div className="ticket-detail"><span className="detail-label">Assigned To:</span><span className="detail-value">{ticket.assignedTo || 'Unassigned'}</span></div>
                  </div>
                  {ticket.dispatchedAt && (
                    <div className="ticket-dispatched-info">
                      <Send size={14} className="dispatch-icon" />
                      <span className="dispatch-label">Dispatched:</span>
                      <span className="dispatch-time">{new Date(ticket.dispatchedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {ticket.acknowledgedAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(0, 240, 255, 0.3)', background: 'rgba(0, 240, 255, 0.05)'}}>
                      <Check size={14} className="dispatch-icon" style={{color: '#00f0ff'}} />
                      <span className="dispatch-label" style={{color: '#00f0ff'}}>Acknowledged:</span>
                      <span className="dispatch-time">{new Date(ticket.acknowledgedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {ticket.enRouteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(255, 165, 0, 0.3)', background: 'rgba(255, 165, 0, 0.05)'}}>
                      <Navigation size={14} className="dispatch-icon" style={{color: '#ffa500'}} />
                      <span className="dispatch-label" style={{color: '#ffa500'}}>En Route:</span>
                      <span className="dispatch-time">{new Date(ticket.enRouteAt).toLocaleString()}</span>
                    </div>
                  )}
                  {ticket.onSiteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(0, 255, 136, 0.3)', background: 'rgba(0, 255, 136, 0.05)'}}>
                      <MapPin size={14} className="dispatch-icon" style={{color: '#00ff88'}} />
                      <span className="dispatch-label" style={{color: '#00ff88'}}>On Site:</span>
                      <span className="dispatch-time">{new Date(ticket.onSiteAt).toLocaleString()}</span>
                    </div>
                  )}
                  {ticket.workCompleteAt && (
                    <div className="ticket-dispatched-info" style={{borderColor: 'rgba(0, 200, 255, 0.3)', background: 'rgba(0, 200, 255, 0.05)'}}>
                      <CheckCircle size={14} className="dispatch-icon" style={{color: '#00c8ff'}} />
                      <span className="dispatch-label" style={{color: '#00c8ff'}}>Work Complete:</span>
                      <span className="dispatch-time">{new Date(ticket.workCompleteAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showEditModal && selectedTicket && (
        <TicketFormModal
          device={{
            id: selectedTicket.deviceId,
            type: selectedTicket.deviceType,
            status: selectedTicket.issueType,
            location: selectedTicket.location,
            model: selectedTicket.model || '',
            lastSeen: selectedTicket.lastSeen || ''
          }}
          subscriber={{ name: selectedTicket.subscriberName }}
          onClose={() => { setShowEditModal(false); setSelectedTicket(null); }}
          onSubmit={handleUpdateTicket}
          editTicket={selectedTicket}
        />
      )}

      {showCloseModal && selectedTicket && (
        <CloseTicketModal
          ticket={selectedTicket}
          onClose={() => { setShowCloseModal(false); setSelectedTicket(null); }}
          onConfirm={handleCloseTicket}
        />
      )}
    </div>
  );
};

export default AllActiveTicketsPage;
